// Postaví stromy podskupin ze VŠECH uložených skupin (saved_station_group) najednou — pro filtr
// „Skupina" ve výběru 1 stanice (StationSelectLayout). Zapouzdřuje tu samou logiku resolvování
// memberCodes → station_group.id → gh_id, jakou dělá StationGroupLayout inline pro jednu skupinu.
//
// Vrací [{ id, name, tree }, ...] kde tree = pole uzlů { label, ghIds: Set, children: [] }
// (stejný tvar jako GroupTree).
//
// Cache: hotový buildGroupTree se ukládá do IndexedDB (viz groupTreeCache.js). Skupiny, které jsou
// v cache, se z ní jen načtou — jejich členství (station_group_member) se vůbec nestahuje a
// buildGroupTree/k-means se pro ně nespouští. Přepočet běží jen pro skupiny, které v cache nejsou
// (nová/změněná skupina, prošlá TTL).
import { useMemo, useState, useEffect } from 'react'
import { useAllStations } from './useAppData'
import { useSavedStationGroups, useStationGroupsBySources, useStationGroupMembers } from './useStationGroups'
import { buildGroupTree, unionNodeGhIds, findNodeByPath } from '../utils/computeGroupTree'
import { groupTreeCacheKey, getCachedGroupTree, setCachedGroupTree } from '../utils/groupTreeCache'

// enabled — dokud je false, hook nedělá žádné síťové dotazy, čtení cache ani stavbu stromů (vrací []).
// Aktivuje se teprve, když uživatel filtr „Skupina" poprvé rozbalí (viz FiltersPanel), aby se
// načítání /select nezdržovalo prací, kterou většina uživatelů nepoužije.
export function useSavedGroupTrees(enabled = true) {
  const { allStations } = useAllStations()
  const savedGroups = useSavedStationGroups(enabled)

  const groupLevelsById = useMemo(() => {
    const map = new Map()
    if (!enabled) return map
    for (const g of savedGroups) map.set(g.id, g.definition?.groupLevels || [])
    return map
  }, [savedGroups, enabled])

  // 1. fáze — zkus načíst hotové stromy z IndexedDB. cachedTrees: Map<savedGroupId, tree>.
  // cacheResolved: až true, víme s jistotou, které skupiny je potřeba dopočítat.
  const [cachedTrees, setCachedTrees] = useState(() => new Map())
  const [cacheResolved, setCacheResolved] = useState(false)

  const cacheKeyById = useMemo(() => {
    const map = new Map()
    for (const [id, groupLevels] of groupLevelsById) {
      if (groupLevels.length === 0) continue
      map.set(id, groupTreeCacheKey(id, groupLevels))
    }
    return map
  }, [groupLevelsById])

  const cacheKeySig = useMemo(() => [...cacheKeyById.values()].sort().join('|'), [cacheKeyById])

  useEffect(() => {
    if (!enabled || cacheKeyById.size === 0) {
      setCachedTrees(new Map())
      setCacheResolved(enabled)
      return
    }
    let cancelled = false
    setCacheResolved(false)
    Promise.all(
      [...cacheKeyById].map(async ([id, key]) => [id, await getCachedGroupTree(key)])
    ).then(pairs => {
      if (cancelled) return
      const map = new Map()
      for (const [id, tree] of pairs) if (tree) map.set(id, tree)
      setCachedTrees(map)
      setCacheResolved(true)
    })
    return () => { cancelled = true }
  }, [enabled, cacheKeySig]) // eslint-disable-line react-hooks/exhaustive-deps

  // 2. fáze — jen pro skupiny, které v cache NEJSOU: posbírej zdroje a členství pro buildGroupTree.
  const missingIds = useMemo(() => {
    if (!cacheResolved) return []
    return [...cacheKeyById.keys()].filter(id => !cachedTrees.has(id))
  }, [cacheResolved, cacheKeyById, cachedTrees])

  const missingGroupLevels = useMemo(
    () => missingIds.map(id => groupLevelsById.get(id) || []),
    [missingIds, groupLevelsById]
  )

  const staticSourceIds = useMemo(() => {
    const ids = new Set()
    for (const groupLevels of missingGroupLevels) {
      for (const level of groupLevels) {
        if (level.dynamicDefType || level.sourceId == null) continue
        ids.add(level.sourceId)
      }
    }
    return [...ids]
  }, [missingGroupLevels])

  const { data: sourceGroups, isReady: sourcesReady } = useStationGroupsBySources(staticSourceIds, { withStatus: true })
  const codeToId = useMemo(() => {
    const map = new Map()
    for (const g of sourceGroups) map.set(`${g.source_id}:${g.code}`, g.id)
    return map
  }, [sourceGroups])

  const staticMemberIds = useMemo(() => {
    const ids = new Set()
    for (const groupLevels of missingGroupLevels) {
      for (const level of groupLevels) {
        if (level.dynamicDefType) continue
        for (const bucket of level.buckets || []) {
          for (const code of bucket.memberCodes || []) {
            const id = codeToId.get(`${level.sourceId}:${code}`)
            if (id != null) ids.add(id)
          }
        }
      }
    }
    return [...ids]
  }, [missingGroupLevels, codeToId])

  const { data: memberRows, isReady: membersReady } = useStationGroupMembers(staticMemberIds, { withStatus: true })
  const memberGhIds = useMemo(() => {
    const map = new Map()
    for (const row of memberRows) {
      if (!map.has(row.group_id)) map.set(row.group_id, [])
      map.get(row.group_id).push(row.gh_id)
    }
    return map
  }, [memberRows])

  // stationsByGhId — potřebné jen pro dynamické úrovně; stromy se počítají z nefiltrované množiny
  // stanic (filtr „Skupina" se aplikuje jako průnik až v applyFilters, aby clustery byly stabilní).
  const stationsByGhId = useMemo(
    () => (missingIds.length > 0 ? new Map(allStations.map(s => [s.gh_id, s])) : new Map()),
    [allStations, missingIds.length]
  )

  // Dopočítej chybějící stromy — až když jsou vstupní data (zdroje + členství) načtená, jinak by
  // se do cache uložil neúplný strom.
  const inputsReady = missingIds.length === 0 || (sourcesReady && membersReady && allStations.length > 0)
  const computedTrees = useMemo(() => {
    const map = new Map()
    if (!inputsReady) return map
    for (const id of missingIds) {
      const groupLevels = groupLevelsById.get(id) || []
      const tree = buildGroupTree(groupLevels, memberGhIds, stationsByGhId, codeToId)
      map.set(id, tree)
    }
    return map
  }, [inputsReady, missingIds, groupLevelsById, memberGhIds, stationsByGhId, codeToId])

  useEffect(() => {
    for (const [id, tree] of computedTrees) {
      const key = cacheKeyById.get(id)
      if (key && tree.length > 0) setCachedGroupTree(key, tree)
    }
  }, [computedTrees, cacheKeyById])

  return useMemo(() => {
    const result = []
    for (const g of savedGroups) {
      const tree = cachedTrees.get(g.id) ?? computedTrees.get(g.id)
      if (!tree || tree.length === 0) continue
      result.push({ id: g.id, name: g.name, tree })
    }
    return result
  }, [savedGroups, cachedTrees, computedTrees])
}

// Sjednocení gh_id všech vybraných cest. Cesta = [savedGroupId, bucketLabel, bucketLabel, ...].
// Vrací Set<gh_id>, nebo null když nic není vybráno (→ filtr se v applyFilters přeskočí).
export function resolveGroupPaths(trees, groupPaths) {
  if (!groupPaths || groupPaths.length === 0) return null
  const treeById = new Map(trees.map(t => [t.id, t.tree]))
  const result = new Set()
  for (const path of groupPaths) {
    const [groupId, ...labels] = path
    const tree = treeById.get(groupId)
    if (!tree) continue
    if (labels.length === 0) {
      // celá uložená skupina = sjednocení všech kořenových uzlů
      for (const node of tree) for (const gh of unionNodeGhIds(node)) result.add(gh)
      continue
    }
    const node = findNodeByPath(tree, labels.map(bucketLabel => ({ bucketLabel })))
    if (!node) continue
    for (const gh of unionNodeGhIds(node)) result.add(gh)
  }
  return result
}
